import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Settings, Cake, Printer } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const INCLUDED = ['Topper', 'Candles'];

export function printCakeCard(order, business) {
  const qc = (order.items || []).map((i) => i.meta?.quick_cake).find(Boolean) || {};
  const paid = (order.payments || []).reduce((s, p) => s + Number(p.amount || 0), 0);
  const balance = order.balance != null ? order.balance : Math.max(0, Number(order.grand_total || 0) - paid);
  const row = (label, val) => `<div class="row"><span class="lbl">${label}</span><span class="val">${val || '—'}</span></div>`;
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${order.invoice_no || 'Cake Order'}</title>
  <style>
    @page { size: A5; margin: 10mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #2b2118; margin: 0; }
    .card { border: 2px solid #e0b400; border-radius: 12px; padding: 18px 22px; }
    .head { text-align: center; border-bottom: 2px dashed #e0b400; padding-bottom: 10px; margin-bottom: 12px; }
    .brand { font-size: 18px; font-weight: 800; letter-spacing: .5px; }
    .title { font-size: 13px; color: #9a7b1a; text-transform: uppercase; letter-spacing: 2px; margin-top: 2px; }
    .row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 14px; border-bottom: 1px dotted #ddd; }
    .lbl { color: #7a6a55; }
    .val { font-weight: 700; text-align: right; }
    .sec { margin-top: 14px; }
    .sec h4 { margin: 0 0 6px; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #9a7b1a; }
    .pill { display: inline-block; background: #fff6d6; border: 1px solid #e7c64d; border-radius: 999px; padding: 3px 12px; font-size: 13px; font-weight: 700; }
    .inc { font-size: 12px; color: #5b4d3a; }
    .money { margin-top: 14px; border-top: 2px solid #2b2118; padding-top: 8px; }
    .money .row { border: none; }
    .grand .val, .grand .lbl { font-size: 16px; font-weight: 800; }
    .bal .val { color: #c0392b; }
  </style></head><body>
    <div class="card">
      <div class="head">
        <div class="brand">${business?.legal_name || 'ROCH Cake Studio'}</div>
        <div class="title">Quick Cake Order</div>
      </div>
      ${row('Order No', order.invoice_no)}
      ${row('Name', order.customer_name)}
      ${row('Mobile', order.customer_phone)}
      ${row('Order Date', new Date(order.created_at || Date.now()).toLocaleDateString('en-IN'))}
      ${row('Pickup', `${qc.pickup_date || ''} ${qc.pickup_time || ''}`.trim())}
      <div class="sec"><h4>Size</h4><span class="pill">${qc.size || '—'}${qc.servings ? ` · ${qc.servings}` : ''}</span></div>
      <div class="sec"><h4>Flavour</h4><span class="pill">${qc.flavour || '—'}</span></div>
      <div class="sec"><h4>Design</h4><span class="pill">${qc.design || '—'}</span></div>
      ${qc.message ? `<div class="sec"><h4>Message on Cake</h4><div class="val" style="text-align:left">${qc.message}</div></div>` : ''}
      <div class="sec"><h4>Included with every cake</h4><div class="inc">✓ ${INCLUDED.join('  ✓ ')}</div></div>
      <div class="money">
        <div class="row grand"><span class="lbl">Amount</span><span class="val">${fmt(order.grand_total)}</span></div>
        <div class="row"><span class="lbl">Advance Received</span><span class="val">${fmt(paid)}</span></div>
        <div class="row bal"><span class="lbl">Balance</span><span class="val">${fmt(balance)}</span></div>
      </div>
    </div>
  </body></html>`;
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open(); doc.write(html); doc.close();
  setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); setTimeout(() => document.body.removeChild(iframe), 1000); }, 250);
}

const Pick = ({ active, onClick, testid, children }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`px-4 py-2.5 rounded-lg border text-sm font-semibold transition-colors text-left ${active ? 'bg-primary text-white border-primary' : 'bg-white text-stone-700 border-stone-300 hover:border-secondary'}`}
  >
    {children}
  </button>
);

export function QuickCakeBuilder({ options, locationId, business, onComplete, canManage, reloadOptions }) {
  const { sizes = [], flavours = [], designs = [] } = options || {};
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [size, setSize] = useState(null);
  const [flavour, setFlavour] = useState('');
  const [design, setDesign] = useState('');
  const [message, setMessage] = useState('');
  const [advance, setAdvance] = useState('');
  const [payMode, setPayMode] = useState('cash');
  const [processing, setProcessing] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const amount = size?.price || 0;
  const balance = Math.max(0, amount - Number(advance || 0));

  const reset = () => {
    setName(''); setMobile(''); setPickupDate(''); setPickupTime('');
    setSize(null); setFlavour(''); setDesign(''); setMessage(''); setAdvance(''); setPayMode('cash');
  };

  const create = async () => {
    if (!size) return toast.error('Select a size');
    if (!flavour) return toast.error('Select a flavour');
    setProcessing(true);
    const adv = Number(advance || 0);
    const payments = adv > 0 ? [{ mode: payMode, amount: adv }] : [];
    const line = {
      item_id: `qc_${Date.now()}`,
      name: `Quick Cake · ${size.label}${flavour ? ` · ${flavour}` : ''}`,
      qty: 1,
      price: amount,
      gst_rate: 5,
      meta: { quick_cake: { size: size.label, servings: size.servings, flavour, design, message, pickup_date: pickupDate, pickup_time: pickupTime, includes: INCLUDED } },
    };
    try {
      const res = await axios.post(`${API}/pos/orders`, {
        location_id: locationId,
        items: [line],
        discount: 0,
        payments,
        customer_name: name || null,
        customer_phone: mobile || null,
      }, auth());
      toast.success(`Cake order created · ${res.data.invoice_no}`);
      printCakeCard(res.data, business);
      reset();
      if (onComplete) onComplete(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create order');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="quick-cake-builder">
      <div className="flex items-center gap-2 mb-4">
        <Cake className="text-primary" size={22} />
        <h2 className="font-heading text-xl font-bold">Quick Cake Order</h2>
        {canManage && (
          <button data-testid="quick-cake-manage" onClick={() => setManageOpen(true)} className="ml-auto inline-flex items-center gap-1 text-sm text-stone-500 hover:text-primary">
            <Settings size={16} /> Manage
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Input data-testid="qc-name" placeholder="Customer name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input data-testid="qc-mobile" placeholder="Mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} />
        <Input data-testid="qc-date" type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} />
        <Input data-testid="qc-time" type="time" value={pickupTime} onChange={(e) => setPickupTime(e.target.value)} />
      </div>

      <div className="mb-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">1 · Size</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {sizes.map((s) => (
            <Pick key={s.qc_id} testid={`qc-size-${s.label}`} active={size?.qc_id === s.qc_id} onClick={() => setSize(s)}>
              <div>{s.label} — {fmt(s.price)}</div>
              {s.servings && <div className="text-xs font-normal opacity-80">{s.servings}</div>}
            </Pick>
          ))}
        </div>
      </div>

      <div className="mb-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">2 · Flavour</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {flavours.map((f) => (
            <Pick key={f.qc_id} testid={`qc-flavour-${f.label}`} active={flavour === f.label} onClick={() => setFlavour(f.label)}>{f.label}</Pick>
          ))}
        </div>
      </div>

      <div className="mb-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">3 · Design</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {designs.map((d) => (
            <Pick key={d.qc_id} testid={`qc-design-${d.label}`} active={design === d.label} onClick={() => setDesign(d.label)}>{d.label}</Pick>
          ))}
        </div>
      </div>

      <div className="mb-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 mb-2">4 · Message on Cake <span className="normal-case font-normal text-stone-400">(max 25 chars)</span></h3>
        <Input data-testid="qc-message" maxLength={25} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. Happy Birthday Aanya" />
      </div>

      <p className="text-xs text-stone-500 mb-5">Included with every cake: <b>{INCLUDED.join(' · ')}</b></p>

      <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
        <div className="flex justify-between text-sm mb-2"><span className="text-stone-600">Amount</span><span className="font-bold">{fmt(amount)}</span></div>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-sm text-stone-600 w-32">Advance Received</span>
          <Input data-testid="qc-advance" type="number" min="0" value={advance} onChange={(e) => setAdvance(e.target.value)} className="h-9 max-w-[140px]" />
          <div className="flex gap-1">
            {['cash', 'upi', 'card'].map((m) => (
              <button key={m} data-testid={`qc-paymode-${m}`} onClick={() => setPayMode(m)} className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize ${payMode === m ? 'bg-primary text-white' : 'bg-stone-100 text-stone-600'}`}>{m}</button>
            ))}
          </div>
        </div>
        <div className="flex justify-between text-sm border-t border-stone-200 pt-2"><span className="text-stone-600">Balance</span><span className="font-bold text-functional-error">{fmt(balance)}</span></div>
      </div>

      <Button data-testid="qc-create" onClick={create} disabled={processing} className="w-full mt-4 bg-primary text-white h-12 text-base">
        <Printer size={18} className="mr-2" /> {processing ? 'Creating…' : 'Create Order & Print Card'}
      </Button>

      {manageOpen && <QuickCakeManage open={manageOpen} onOpenChange={setManageOpen} options={options} reload={reloadOptions} />}
    </div>
  );
}

function QuickCakeManage({ open, onOpenChange, options, reload }) {
  const [kind, setKind] = useState('size');
  const [label, setLabel] = useState('');
  const [servings, setServings] = useState('');
  const [price, setPrice] = useState('');
  const list = kind === 'size' ? options.sizes : kind === 'flavour' ? options.flavours : options.designs;

  const add = async () => {
    if (!label.trim()) return;
    const body = { kind, label: label.trim() };
    if (kind === 'size') { body.servings = servings.trim(); body.price = parseFloat(price || 0); }
    try { await axios.post(`${API}/quick-cake/options`, body, auth()); setLabel(''); setServings(''); setPrice(''); toast.success('Added'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this option?')) return;
    try { await axios.delete(`${API}/quick-cake/options/${id}`, auth()); toast.success('Deleted'); reload(); }
    catch (e) { toast.error('Failed'); }
  };
  const editPrice = async (o) => {
    const np = window.prompt(`New price for ${o.label}`, o.price);
    if (np == null) return;
    try { await axios.put(`${API}/quick-cake/options/${o.qc_id}`, { price: parseFloat(np) }, auth()); toast.success('Updated'); reload(); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="quick-cake-manage-dialog">
        <DialogHeader><DialogTitle>Manage Quick Cake Options</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          {[['size', 'Sizes'], ['flavour', 'Flavours'], ['design', 'Designs']].map(([k, l]) => (
            <button key={k} data-testid={`qcm-tab-${k}`} onClick={() => setKind(k)} className={`px-3 py-1.5 rounded-md text-sm font-semibold ${kind === k ? 'bg-primary text-white' : 'bg-stone-100 text-stone-600'}`}>{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          <Input data-testid="qcm-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={kind === 'size' ? 'e.g. 2kg' : 'Name'} className="flex-1 min-w-[120px]" />
          {kind === 'size' && <Input data-testid="qcm-servings" value={servings} onChange={(e) => setServings(e.target.value)} placeholder="Serves 16-20" className="w-32" />}
          {kind === 'size' && <Input data-testid="qcm-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Price" className="w-24" />}
          <Button data-testid="qcm-add" onClick={add} className="bg-primary text-white"><Plus size={16} /></Button>
        </div>
        <div className="space-y-2 mt-2 max-h-72 overflow-y-auto">
          {list.map((o) => (
            <div key={o.qc_id} className="flex items-center gap-2 border border-stone-200 rounded-sm px-3 py-2">
              <span className="flex-1 text-sm">{o.label}{o.kind === 'size' && <span className="text-stone-400 text-xs"> · {o.servings} · {fmt(o.price)}</span>}</span>
              {o.kind === 'size' && <button data-testid={`qcm-edit-${o.qc_id}`} onClick={() => editPrice(o)} className="text-stone-400 hover:text-primary"><Pencil size={14} /></button>}
              <button data-testid={`qcm-del-${o.qc_id}`} onClick={() => del(o.qc_id)} className="text-stone-400 hover:text-functional-error"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
